import React from 'react';
import { Link } from 'react-router-dom';
import ArticleShell from './ArticleShell';
import { H2, P, Callout, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const GOAML_LINK = (
  <Link to="/services/goaml-compliance" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900" data-testid="fiu-guide-goaml-link">
    goAML registration and reporting support
  </Link>
);

const ORDERS = [
  ['Suspension order', 'Suspends a transaction and prevents related transactions that could circumvent the suspension.', 'Up to 10 business days'],
  ['Freezing order', 'Freezes the funds, accounts or business relationships identified in the order.', 'Up to 30 days, subject to extension by the Attorney General or delegate'],
  ['Monitoring order', 'Requires monitoring and reporting on specified transactions or business relationships.', 'Up to 30 days']
];

const PREP = [
  ['AML policies and procedures', 'Include a clear process for identifying and escalating situations that may require a PSTR.'],
  ['Compliance officer responsibilities', 'Assign responsibility for receiving, implementing, monitoring and reporting on UAEFIU orders.'],
  ['goAML and communication access', 'Check that authorised personnel can access goAML and the relevant FIU communication channels.'],
  ['Response arrangements', 'Establish escalation procedures for urgent matters, including outside business hours where required by the regulation.'],
  ['Employee training', 'Help staff recognise urgent risk indicators and understand their responsibilities concerning confidentiality and tipping-off.'],
  ['Independent testing', 'Periodically test whether the procedures work effectively.'],
  ['Recordkeeping', 'Document the actions taken and retain the required records for at least five years from the end of the business relationship.']
];

const OrdersTable = () => (
  <div className="overflow-x-auto my-6 rounded-xl border border-gray-200" data-testid="fiu-guide-orders-table">
    <table className="w-full min-w-[560px] text-sm text-left">
      <thead className="bg-gray-900 text-white">
        <tr>{['Order', 'Purpose', 'Maximum initial period'].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {ORDERS.map((r) => (
          <tr key={r[0]}>{r.map((c, i) => <td key={i} className={`px-4 py-3 align-top ${i === 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const UAEFIURegulation1of2026Guide = () => {
  const guide = {
    ...getGuideBySlug('uae-fiu-regulation-1-2026-suspicious-transactions-freezing-funds'),
    relatedService: { label: 'goAML & AML Compliance', path: '/services/goaml-compliance' }
  };

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'who', label: 'Who Should Pay Attention?' },
    { id: 'pstr', label: 'What Is a PSTR?' },
    { id: 'example', label: 'A Practical Example' },
    { id: 'threshold', label: 'Is AED 500,000 the Minimum?' },
    { id: 'pstr-vs-str', label: 'Does a PSTR Replace an STR?' },
    { id: 'after-pstr', label: 'What Happens After a PSTR?' },
    { id: 'orders', label: 'What Orders Can the UAEFIU Issue?' },
    { id: 'customer', label: 'Can the Customer Be Informed?' },
    { id: 'prepare', label: 'What Should Businesses Do Now?' },
    { id: 'wpaa-help', label: 'How WPAA Can Support You' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-fiu-regulation" ctaService="goaml">
      <Callout variant="info" title="Effective 14 December 2026">
        The UAEFIU has announced that Regulation No. 1 of 2026 will take effect on <strong>14 December 2026</strong>.
        Reporting entities should use the period before implementation to review their policies, systems and staff training.
      </Callout>

      <H2 id="overview">Overview</H2>
      <P>
        The UAE Financial Intelligence Unit (UAEFIU) has issued Regulation No. 1 of 2026, setting out procedures for the
        postponement or suspension of suspicious transactions and the freezing of funds.
      </P>
      <P>
        The regulation is relevant to financial institutions, designated non-financial businesses and professions (DNFBPs),
        and virtual asset service providers (VASPs). It introduces an urgent reporting process for situations where
        suspicious funds may soon be transferred, withdrawn or otherwise dissipated.
      </P>

      <H2 id="who">Who Should Pay Attention?</H2>
      <P>The regulation applies to reporting entities, including:</P>
      <BulletList items={[
        'Financial institutions;',
        'DNFBPs, such as relevant real estate businesses, dealers in precious metals and precious stones, auditors and certain legal and corporate service providers; and',
        'Virtual asset service providers.'
      ]} />
      <P>
        Businesses should assess whether they fall within the regulated categories based on their licensed activities and
        the applicable UAE AML/CFT framework.
      </P>

      <H2 id="pstr">What Is a Postponement Suspicious Transaction Report?</H2>
      <P>
        A Postponement Suspicious Transaction Report (PSTR) is an urgent report submitted to the UAEFIU through the goAML platform.
      </P>
      <P>
        A reporting entity must file a PSTR promptly when it suspects, or has reasonable grounds to suspect, that a
        transaction, attempted transaction or funds are linked to crime and that there is an imminent risk that the funds
        will be transferred, withdrawn or dissipated.
      </P>
      <Callout variant="warning" title="“Promptly” means within 24 hours">
        An attempted transaction can include an instruction that has been initiated but not settled, including a scheduled
        or queued transaction.
      </Callout>

      <H2 id="example">A Practical Example</H2>
      <P>
        A DNFBP identifies circumstances giving rise to reasonable suspicion about the source of funds involved in a
        transaction. It also becomes aware that the funds are expected to be transferred shortly, creating an imminent risk
        that they may no longer be traceable or available.
      </P>
      <P>
        The business should escalate the matter immediately to its compliance officer. Where the conditions in the
        regulation are met, the compliance officer should arrange for a PSTR to be submitted promptly through goAML.
      </P>

      <H2 id="threshold">Is AED 500,000 the Minimum Amount for Reporting?</H2>
      <P>
        No. The regulation mentions AED 500,000 as one possible indicator when assessing whether to file a PSTR, including
        where several smaller transactions appear to be linked.
      </P>
      <P>
        However, the amount is indicative rather than a minimum threshold. A PSTR may be required for a lower amount where
        the relevant conditions and risk indicators are present.
      </P>
      <P>
        The regulation also identifies certain circumstances where the AED 500,000 figure does not apply, including
        suspected links to terrorist financing, organised crime, third-party money laundering and certain higher-threat
        predicate offences.
      </P>
      <P>
        Businesses should therefore assess the nature of the suspicion and the urgency, rather than rely on the
        transaction value alone.
      </P>

      <H2 id="pstr-vs-str">Does a PSTR Replace an STR?</H2>
      <P>
        No. A PSTR is a specific form of urgent suspicious transaction reporting. It does not replace existing obligations
        to file Suspicious Transaction Reports (STRs) or other applicable reports.
      </P>
      <P>
        For example, a transaction may raise suspicion without presenting an imminent risk that funds will be moved. In
        that case, a PSTR may not be appropriate, but the business must still consider its existing suspicious reporting
        obligations.
      </P>

      <H2 id="after-pstr">What Happens to a Transaction After a PSTR?</H2>
      <P>
        Under the regulation, a reporting entity must generally refrain from carrying out a transaction that is subject to
        a PSTR. The regulation provides exceptions, including where:
      </P>
      <BulletList items={[
        'Three business days have passed since filing the PSTR and the entity has received no communication from the UAEFIU;',
        'The UAEFIU authorises the transaction to proceed; or',
        'It is objectively impossible to refrain from carrying out the transaction, or refraining would be likely to frustrate efforts to pursue the customer or intended beneficiary.'
      ]} />
      <P>Where the last exception applies, the PSTR must be filed within two hours after the transaction is carried out.</P>
      <P>
        The practical steps will depend on the reporting entity’s role. For example, a bank may be able to stop a payment
        it is processing, while an auditor reviewing a client’s records may not control the client’s bank account. Each
        entity should follow the regulation and any applicable UAEFIU instructions.
      </P>

      <H2 id="orders">What Orders Can the UAEFIU Issue?</H2>
      <P>The regulation addresses three types of order:</P>
      <OrdersTable />
      <P>
        Suspension and freezing orders are immediately binding. Reporting entities must have procedures to recognise and
        act on them quickly.
      </P>
      <Callout variant="warning" title="Two-hour acknowledgement">
        The regulation generally requires acknowledgement of a suspension or written freezing order within two hours,
        subject to the specified exceptions concerning activity outside business hours. If an entity cannot implement an
        order, or implements it late, it must provide a written explanation and supporting information within the
        applicable two-hour period.
      </Callout>

      <H2 id="customer">Can the Customer Be Informed?</H2>
      <P>Customer communication depends on the type of order.</P>
      <P>
        For a <strong>suspension order</strong>, the reporting entity must not disclose the order, the submission of a PSTR
        or STR, or other information that could tip off the customer, unless the UAEFIU expressly approves disclosure in writing.
      </P>
      <P>
        For a <strong>freezing order</strong>, the regulation requires the reporting entity to notify the customer after
        the freeze has been executed, explain that the order was issued by the UAEFIU and request documents supporting the
        legitimacy of the transaction and the lawful origin of the funds.
      </P>
      <P>Staff should follow the applicable order and UAEFIU instructions carefully before communicating with a customer.</P>

      <H2 id="prepare">What Should Businesses Do Now?</H2>
      <P>Reporting entities can prepare for the regulation by reviewing the following areas:</P>
      <BulletList items={PREP.map(([t, d]) => <><strong>{t}:</strong> {d}</>)} />
      <P>
        The appropriate procedures should reflect the business’s activities, operational hours and its ability to handle
        or control transactions and funds.
      </P>

      <H2 id="wpaa-help">How WPAA Can Support Your Business</H2>
      <P>
        At White Paper Accounts Auditing, we support UAE businesses in strengthening their AML/CFT compliance arrangements.
        Our services include:
      </P>
      <BulletList items={[
        'AML policies and procedures review;',
        'Enterprise-wide risk assessments;',
        'AML internal audit;',
        'Compliance training and awareness;',
        <>{GOAML_LINK}; and</>,
        'Review of customer due diligence and recordkeeping processes.'
      ]} />
      <P>
        If you would like to understand how the regulation affects your business, contact WPAA to discuss your existing
        compliance arrangements and the steps needed to prepare.
      </P>

      <H2>Source</H2>
      <P>
        UAEFIU Regulation No. 1 of 2026, Regarding the System Governing the Postponement or Suspension of Suspicious
        Transactions and the Freezing of Funds.
      </P>

      <Callout variant="warning" title="Disclaimer">
        This article is for general information and does not constitute legal advice. Businesses should consider the
        regulation, subsequent UAEFIU guidance and their own circumstances when determining their obligations.
      </Callout>
    </ArticleShell>
  );
};

export default UAEFIURegulation1of2026Guide;
